import { format } from 'date-fns'
import { useFormik } from 'formik'
import { forIn } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { AccountDetails, Correspondent } from '../../types'
import { RoutesEnum } from '../../types/RoutesEnum'
import { formatPayload } from './helpers/getPayload'
import { tradeEntrySchema } from './tradeEntrySchema'
import TradeForm, { TradeFormProps } from './TradeForm'
import TradePreview from './TradePreview'

import {
  AccountTypeEnum,
  CurrencyEnum,
  InstrumentTypeEnum,
  listingCountryEnum,
  MarketEnum,
  RouteTypeEnum,
  SecurityCodeTypeEnum,
  SettlementTypeEnum,
  SubmissionPayload,
  TradeFormValues,
  TradeTypeEnum,
} from './Types'

export interface FormWrapperProps extends RouteComponentProps {
  accountDetails: AccountDetails
  correspondent: Correspondent
  postTransaction: (payload: SubmissionPayload, successCallback: () => void) => void
  tradeSubmissionFailedMessage?: string
}

const TradeFormWrapper: React.FunctionComponent<FormWrapperProps> = ({
  accountDetails,
  correspondent,
  postTransaction,
  tradeSubmissionFailedMessage,
  location,
  history,
}: FormWrapperProps) => {
  const [CurrentView, setCurrentView] = useState<(props: TradeFormProps) => JSX.Element>(() => TradeForm)
  const tradeFormValuesKey = `tradeFormValues-${accountDetails.accountNumber}`

  const formik = useFormik({
    initialValues: {
      accountType: accountDetails.accountType || AccountTypeEnum.Cash,
      repCode: accountDetails.repCode || '',
      securityCodeType: SecurityCodeTypeEnum.tickerSymbol,
      symbol: '',
      action: undefined,
      quantity: undefined,
      executionPrice: undefined,
      capacity: undefined,
      routeType: undefined,
      instrumentType: InstrumentTypeEnum.equity,
      tradeType: TradeTypeEnum.execution,
      listingCountry: listingCountryEnum.UnitedStates,
      currency: CurrencyEnum.usd,
      tradeDate: format(new Date(), 'MM/dd/yyyy'),
      tradeTime: undefined,
      settlementType: SettlementTypeEnum.regularSettlement,
      settlementDate: '',
      memo: '',
      executionAlgorithm: undefined,
      sourceAccountNumber: undefined,
      sourceAccountType: undefined,
      executingBroker: undefined,
      specialInstructions: undefined,
      lotMatchingInstructions: undefined,
    } as TradeFormValues,
    onSubmit: () => {
      history.push(`${location.pathname}?preview=true`, { [tradeFormValuesKey]: formik.values })
    },
    validationSchema: () => tradeEntrySchema(formik, accountDetails),
  })

  const saveFormStateToLocation = (search: string = location.search) => {
    const { pathname } = location
    history.replace(`${pathname}${search}`, { [tradeFormValuesKey]: formik.values })
  }

  useEffect(() => {
    if (formik.dirty) {
      saveFormStateToLocation()
    }
    // eslint-disable-next-line
  }, [formik.values, formik.dirty])

  useEffect(() => {
    const { pathname, search, state } = location
    // @ts-ignore
    const tradeFormValues = (state || {})[tradeFormValuesKey]

    const updateFormikFromLocationState = () => {
      if (tradeFormValues && !formik.dirty) {
        forIn(tradeFormValues, (value, key) => {
          setFieldValue(key, value)
        })
      }
    }

    const hasFormikErrors = () => Object.keys(formik.errors).length > 0

    if (pathname === `/${RoutesEnum.TradeEntry}/${accountDetails.accountNumber}`) {
      updateFormikFromLocationState()
      if (search === '?preview=true') {
        if (CurrentView.name === TradeForm.name) {
          if (hasFormikErrors()) {
            saveFormStateToLocation('')
          } else {
            setCurrentView(() => TradePreview)
          }
        }
      } else if (CurrentView.name === TradePreview.name) {
        setCurrentView(() => TradeForm)
      }
    }
    // eslint-disable-next-line
  }, [
    location.pathname,
    location.search,
    location.state,
    accountDetails.accountNumber,
    CurrentView.name,
    formik.errors,
  ])

  const onTradeSubmitted = () => history.replace('/')

  const handleSubmit = () => {
    const payload = formatPayload(formik.values, correspondent.code, accountDetails.accountNumber)
    postTransaction(payload, onTradeSubmitted)
  }

  const formReducer = () => {
    const { values } = formik
    if (values.securityCodeType === SecurityCodeTypeEnum.occSymbol) {
      setFieldValue('instrumentType', InstrumentTypeEnum.option)
    }
    if (values.instrumentType === InstrumentTypeEnum.option) {
      setFieldValue('listingCountry', listingCountryEnum.UnitedStates)
    }
    if (values.routeType) {
      if (values.routeType === RouteTypeEnum.Dma || values.routeType === RouteTypeEnum.Away) {
        if (!values.market) {
          setFieldValue('market', MarketEnum.Otc)
        }
      } else {
        setFieldValue('market', undefined)
        setFieldValue('executingBroker', undefined)
      }
    }
    if (values.market && values.market !== MarketEnum.Otc) {
      setFieldValue('executingBroker', undefined)
    }
  }

  const formReducerMemo = useCallback(formReducer, [
    formik.values.securityCodeType,
    formik.values.instrumentType,
    formik.values.routeType,
    formik.values.tradeType,
    formik.values.capacity,
    formik.values.market,
  ])

  useEffect(() => {
    formReducerMemo()
  }, [formik.values, formReducerMemo])

  const setFieldValue = (field: string, value: any) => {
    formik.setFieldValue(field, value)
  }
  const handleChange = (e: React.ChangeEvent) => {
    formik.handleChange(e)
  }
  const handleBlur = (field: string) => {
    formik.setFieldTouched(field)
  }

  const handleModeButtonClick = () => {
    if (CurrentView.name === TradeForm.name) {
      formik.handleSubmit()
    } else {
      history.goBack()
    }
  }

  return (
    <CurrentView
      accountDetails={accountDetails}
      correspondent={correspondent}
      errors={formik.errors}
      handleBlur={handleBlur}
      handleChange={handleChange}
      onModeButtonClick={handleModeButtonClick}
      onSubmit={handleSubmit}
      setFieldValue={setFieldValue}
      touched={formik.touched}
      tradeSubmissionFailedMessage={tradeSubmissionFailedMessage}
      values={formik.values}
    />
  )
}

export default withRouter(TradeFormWrapper)
